/**
 * Session Engine v3 — canonical session model.
 *
 * Domain model for the five-step Event. Backed by a real
 * prioritization_sessions row; V3 step config stored in mode_config.v3Config.
 */

import type { SituationVector } from '../../types/prioritization/situationVector';

/** The five steps. S1–S4 are the "prelims"; S5 is the "main event". */
export type StepId = 'S1' | 'S2' | 'S3' | 'S4' | 'S5';

/** Each step is either declared (stated) or determined collaboratively (a tool). */
export type StepMode = 'declared' | 'collaborative';

/** Event container shape. */
export type ContainerMode = 'single' | 'multi';

/** Prioritization context — gates which item-input sources are available. */
export type ContextMode = 'isolated' | 'app_linked' | 'cycle_linked';

/** Whether the item pool is still open to mutation. */
export type PoolState = 'open' | 'locked';

/**
 * How a collaborative step is run:
 * - 'live' → synchronous, everyone in the room now, resolved in one sitting.
 * - 'open' → asynchronous; the step stays open as a session collecting input
 *   over time until the facilitator closes it.
 */
export type RunMode = 'live' | 'open';

/** Lifecycle of a collaborative step's run. */
export type RunStatus = 'not_started' | 'running' | 'complete';

/** The five standard item-input sources. */
export type ItemInputSource = 'import' | 'pull' | 'type' | 'platform' | 'collection';

export const STEP_ORDER: StepId[] = ['S1', 'S2', 'S3', 'S4', 'S5'];

export const STEP_META: Record<StepId, { label: string; blurb: string }> = {
  S1: { label: 'Participants', blurb: 'Who is in the room, who decides, who observes' },
  S2: { label: 'Items', blurb: 'The manageable set carried forward' },
  S3: { label: 'Criteria', blurb: 'The dimensions of value and their weights' },
  S4: { label: 'Values', blurb: 'The value scales and the values per item' },
  S5: { label: 'Ranking', blurb: 'The final ordering — the main event' },
};

export interface EngineItem {
  id: string;
  title: string;
  source: ItemInputSource;
}

export type ParticipantRole = 'decider' | 'contributor' | 'observer' | 'facilitator';

export interface ParticipantRef {
  id: string;
  name: string;
  /** Real workspace user UUID — present when selected from the workspace user list. */
  userId?: string;
  role: ParticipantRole;
}

export interface CriterionDef {
  id: string;
  name: string;
  weight: number;
  /**
   * Where this criterion's values come from (migration 1062; src/lib/valuesFrom.ts):
   * the record, one person, the facilitator, the room or the method. Inherited from
   * the track's Criteria & scales row when the session is made on a track; absent
   * when nobody has said, and S4 then behaves as it always did.
   */
  valuesFrom?: 'any_group_member' | 'specific_person' | 'tool_result' | 'facilitator_entry' | 'participant_vote' | null;
  /** The person, when valuesFrom is 'specific_person'. */
  valuesFromPersonId?: string | null;
}

/**
 * A LIVE binding between the cycle's strategy and this event's criteria weights.
 *
 * Before this, driver weights could only be copied into criteria once ("apply
 * proposed weights"). The copy carried no provenance and no back-reference, so
 * the strategy could move afterwards and the session kept scoring on a stale
 * reading of it with nothing on screen to say so — the drivers had effectively
 * become permanent criteria weights of unknown origin.
 *
 * When this is set, criteria weights are recomputed from the strategy's active
 * driver weights whenever either side changes, and the surface says so. It is
 * removable: unlinking keeps the weights exactly where they are and simply
 * stops them following. Weights only — a link never adds or removes a
 * criterion, so criteria governance locks cannot be violated by construction.
 */
export interface DriverWeightLink {
  strategyId: string;
  /** ISO timestamp the link was established, for provenance. */
  linkedAt: string;
}

/**
 * Two exclusivity relations (see SPEC-02):
 * - 'competing'      → exclusive/competing_alternative: may share a pool, but
 *                      at most one should sit above the cut line (warn).
 * - 'cannot_coexist' → proposed pool_exclusive: must not be members of the same
 *                      pool/session at all (block at inclusion + scenario time).
 */
export type ExclusivityKind = 'competing' | 'cannot_coexist';

export interface ExclusivityGroup {
  id: string;
  kind: ExclusivityKind;
  itemIds: string[];
  label?: string;
}

export interface Scenario {
  id: string;
  name: string;
  /** Number of items above the cut line (selected). Items ranked [0, cutLine) are above. */
  cutLine: number;
  /** Snapshot of items above the cut line when the scenario was saved. */
  selectedItemIds: string[];
  notes?: string;
}

export interface StepConfig {
  step: StepId;
  /**
   * The step is IN SCOPE for this event — it appears in the rail and the
   * creator may fill it in. It does NOT mean the event is blocked without it:
   * whether a step is genuinely mandatory is computed from the goal and the
   * chosen tool (`isStepMandatory` / `stepPrerequisites` in engineLogic.ts).
   */
  required: boolean;
  mode: StepMode;
  /** question 1 of the 3-question framing */
  goal?: string;
  /** question 2 — chosen tool when collaborative */
  toolId?: string;
  /** question 3 — how the collaborative run happens (live vs open/async) */
  runMode: RunMode;
  /** question 3 — run lifecycle for a collaborative step */
  runStatus: RunStatus;
}

export type StepHistoryAction = 'run' | 'unlocked' | 'revised' | 'reset';

export interface StepHistoryEntry {
  step: StepId;
  action: StepHistoryAction;
  timestamp: string;
  /** Optional note recorded at the time of the action (e.g. reason for unlocking) */
  note?: string;
}

export interface SessionEvent {
  /** Real prioritization_sessions UUID once persisted to Supabase. */
  id: string;
  title: string;
  jobId?: string;
  /**
   * The step the facilitator chose as their goal on the setup screen.
   * Drives which steps are required and what gets labeled "Main Event".
   */
  primaryGoalStep?: StepId;
  containerMode: ContainerMode;
  contextMode: ContextMode;
  /** The Track this Event operates within. Set when contextMode === 'cycle_linked'. */
  cycleId?: string;
  cycleName?: string;
  trackId?: string;
  trackName?: string;
  /** Declared description of the room, mirrored from prioritization_sessions.situation_vector. */
  situationVector?: SituationVector | null;
  poolState: PoolState;
  steps: Record<StepId, StepConfig>;
  participants: ParticipantRef[];
  items: EngineItem[];
  criteria: CriterionDef[];
  /** itemId -> criterionId -> value, on the session's value range (tool_config scoring_scale_min/max) */
  values: Record<string, Record<string, number>>;
  /** Ordered item ids — the S5 output. */
  ranking: string[];
  scenarios: Scenario[];
  activeScenarioId?: string;
  /** Parent Event map: collaborative step -> child prioritization_sessions.id. */
  childSessions: Partial<Record<StepId, string>>;
  /** Audit trail of step lifecycle changes (runs, unlocks, revisions). */
  stepHistory: StepHistoryEntry[];
  /**
   * When set, this event's criteria weights follow the linked strategy's driver
   * weights live. Absent = weights are the room's own (the default).
   */
  driverWeightLink?: DriverWeightLink;
  /** True while the initial Supabase row is being created. */
  persisting?: boolean;
}
